<template>
  <div class="page-content">
    <breadcrumb-index 
      :items="breadcrumbs"
      icon="file-text"
      :badge="exams.length ? `${exams.length} exams` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button v-if="canModify" @click="openCreateModal" class="btn btn-primary btn-sm">
          <vue-feather type="plus" size="14" class="me-1" />
          Create Exam
        </button>
      </template>
    </breadcrumb-index>

    <DataTable
      :items="filteredExams"
      :columns="columns"
      :loading="loading"
      search-placeholder="Search exams..."
      empty-icon="file-text"
      empty-title="No exams found"
      empty-description="Create your first exam to get started"
    >
      <template #filters>
        <select v-model="filterStatus" class="filter-select-modern">
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
        <select v-model="filterCategory" class="filter-select-modern">
          <option value="">All Categories</option>
          <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
        </select>
      </template>

      <template #cell-exam="{ item }">
        <div class="avatar-info">
          <span class="avatar-name">{{ item.name }}</span>
          <span v-if="item.description" class="avatar-subtitle">{{ truncate(item.description, 50) }}</span>
        </div>
      </template>

      <template #cell-category="{ item }">
        <span class="badge bg-light text-dark">{{ item.category || '-' }}</span>
      </template>

      <template #cell-subjects="{ item }">
        <router-link :to="`/app/exams/${item.id}/subjects`" class="badge bg-primary text-decoration-none">
          {{ item.subjectCount || 0 }} subjects
        </router-link>
      </template>

      <template #cell-testSeries="{ item }">
        <router-link :to="`/app/exams/${item.id}/test-series`" class="badge bg-info text-decoration-none">
          {{ item.testSeriesCount || 0 }} series
        </router-link>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', getStatusClass(item.status)]">
          {{ item.status }}
        </span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <router-link 
            :to="`/app/exams/${item.id}/subjects`" 
            class="action-btn action-view" 
            v-tooltip="'View subjects'"
          >
            <vue-feather type="book" size="14" />
          </router-link>
          <router-link 
            :to="`/app/exams/${item.id}/test-series`" 
            class="action-btn" 
            v-tooltip="{ text: 'Test series', variant: 'primary' }"
            style="color: #0ea5e9;"
          >
            <vue-feather type="layers" size="14" />
          </router-link>
          <button 
            v-if="canModify"
            class="action-btn action-edit" 
            @click="openEditModal(item)"
            v-tooltip="'Edit exam'"
          >
            <vue-feather type="edit-2" size="14" />
          </button>
          <button 
            v-if="canModify"
            class="action-btn action-delete" 
            @click="confirmDelete(item)" 
            v-tooltip="{ text: 'Delete exam', variant: 'danger' }"
          >
            <vue-feather type="trash-2" size="14" />
          </button>
        </div>
      </template>

      <template #empty-action>
        <button @click="openCreateModal" class="btn btn-primary">
          <vue-feather type="plus" size="16" class="me-1" />
          Create First Exam
        </button>
      </template>
    </DataTable>

    <!-- Create/Edit Modal -->
    <CreateModal
      v-model="showModal"
      :title="isEditing ? 'Edit Exam' : 'Create Exam'"
      icon="file-text"
      :loading="saving"
      :submit-text="isEditing ? 'Update' : 'Create'"
      :submit-disabled="!form.name"
      :item-id="isEditing ? editingId : ''"
      @submit="saveExam"
    >
      <form @submit.prevent="saveExam" class="modal-form">
        <div class="form-group">
          <label class="form-label">Exam Name <span class="text-danger">*</span></label>
          <input 
            v-model="form.name" 
            type="text" 
            class="form-control" 
            placeholder="e.g., UPSC Civil Services"
            required
          />
        </div>

        <div class="form-group">
          <label class="form-label">Description</label>
          <textarea 
            v-model="form.description" 
            class="form-control" 
            rows="3"
            placeholder="Brief description of the exam..."
          ></textarea>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Category</label>
            <input 
              v-model="form.category" 
              type="text" 
              class="form-control" 
              placeholder="e.g., Government, Engineering"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Status</label>
            <select v-model="form.status" class="form-control">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'

export default {
  name: 'ExamsList',
  components: { DataTable, CreateModal },
  setup() {
    const authStore = useAuthStore()
    const { toast, confirmDelete: confirmDeleteAction } = useNotifications()
    const canModify = computed(() => authStore.isSuperAdmin || authStore.isAdmin)
    
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Exams', active: true }
    ]

    const columns = [
      { key: 'exam', label: 'Exam Name', sortable: true, minWidth: '280px' },
      { key: 'category', label: 'Category', sortable: true },
      { key: 'subjects', label: 'Subjects', class: 'text-center' },
      { key: 'testSeries', label: 'Test Series', class: 'text-center' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    const exams = ref([])
    const loading = ref(true)
    const filterStatus = ref('')
    const filterCategory = ref('')

    // Modal state
    const showModal = ref(false)
    const isEditing = ref(false)
    const saving = ref(false)
    const editingId = ref(null)
    const form = reactive({
      name: '',
      description: '',
      category: '',
      status: 'draft'
    })

    const categories = computed(() => {
      const cats = [...new Set(exams.value.map(e => e.category).filter(Boolean))]
      return cats.sort()
    })

    const filteredExams = computed(() => {
      return exams.value.filter(e => {
        const matchesStatus = !filterStatus.value || e.status === filterStatus.value
        const matchesCategory = !filterCategory.value || e.category === filterCategory.value
        return matchesStatus && matchesCategory
      })
    })

    const fetchExams = async () => {
      loading.value = true
      try {
        const response = await api.get('/v1/admin/exams')
        exams.value = response.data.exams || []
      } catch (error) {
        console.error('Failed to fetch exams:', error)
        toast.error('Failed to load exams')
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      form.name = ''
      form.description = ''
      form.category = ''
      form.status = 'draft'
      editingId.value = null
      isEditing.value = false
    }

    const openCreateModal = () => {
      resetForm()
      showModal.value = true
    }

    const openEditModal = (exam) => {
      isEditing.value = true
      editingId.value = exam.id
      form.name = exam.name || ''
      form.description = exam.description || ''
      form.category = exam.category || ''
      form.status = exam.status || 'draft'
      showModal.value = true
    }

    const saveExam = async () => {
      if (!form.name) return
      
      saving.value = true
      try {
        if (isEditing.value) {
          const response = await api.put(`/v1/admin/exams/${editingId.value}`, form)
          const index = exams.value.findIndex(e => e.id === editingId.value)
          if (index !== -1) {
            exams.value[index] = { ...exams.value[index], ...response.data.exam || form }
          }
          toast.success('Exam updated successfully')
        } else {
          const response = await api.post('/v1/admin/exams', form)
          exams.value.unshift(response.data.exam || response.data)
          toast.success('Exam created successfully')
        }
        showModal.value = false
        resetForm()
      } catch (error) {
        toast.error(error.response?.data?.error?.message || 'Failed to save exam')
      } finally {
        saving.value = false
      }
    }

    const getStatusClass = (status) => {
      const classes = {
        draft: 'status-inactive',
        active: 'status-active',
        archived: 'status-pending'
      }
      return classes[status] || 'status-inactive'
    }

    const truncate = (text, length) => {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }

    const confirmDelete = async (exam) => {
      const confirmed = await confirmDeleteAction(
        exam.name,
        'This will delete all subjects, topics, lessons, and tests associated with this exam.'
      )

      if (confirmed) {
        try {
          await api.delete(`/v1/admin/exams/${exam.id}`)
          exams.value = exams.value.filter(e => e.id !== exam.id)
          toast.success(`"${exam.name}" has been deleted`)
        } catch (error) {
          toast.error('Failed to delete exam')
        }
      }
    }

    onMounted(fetchExams)

    return {
      breadcrumbs,
      columns,
      exams,
      loading,
      filterStatus,
      filterCategory,
      categories,
      filteredExams,
      showModal,
      isEditing,
      saving,
      form,
      canModify,
      openCreateModal,
      openEditModal,
      saveExam,
      getStatusClass,
      truncate,
      confirmDelete
    }
  }
}
</script>

<style scoped>
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-control {
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

:global(body.dark-mode) .form-label {
  color: #d1d5db;
}

:global(body.dark-mode) .form-control {
  background: #1e293b;
  border-color: #374151;
  color: #f3f4f6;
}

:global(body.dark-mode) .form-control:focus {
  border-color: #6366f1;
}
</style>
